import { useAtom } from "jotai";
import { Terminal } from "lucide-react";
import { useMemo } from "react";

import { CodeBlock } from "@components/CodeBlock.tsx";
import { MethodChip } from "@components/MethodChip.tsx";
import { cn } from "@lib/cn.ts";
import { envAtom, languageAtom } from "@lib/state.ts";
import type { LanguageSnippet, ServiceEntry, Token } from "@lib/types.ts";

interface Props {
  service: ServiceEntry;
  method: string;
  path: string;
  snippets: LanguageSnippet[] | undefined;
  /** Pre-highlighted response bodies, keyed by status code. */
  responseExamples: { status: string; lines: Token[][] }[];
}

const TOKEN_ENV = "GT_ACCESS_TOKEN";

export function SnippetPanel({
  service,
  method,
  path,
  snippets,
  responseExamples,
}: Props) {
  const [language, setLanguage] = useAtom(languageAtom);
  const [env, setEnv] = useAtom(envAtom);

  // Snippets are generated against the first declared server (production);
  // switching environments is a literal substitution of that base URL.
  const baseServer = service.servers[0];
  const activeServer = service.servers.find((s) => s.env === env) ?? baseServer;

  const replace = useMemo<[string, string][]>(
    () =>
      baseServer && activeServer && baseServer.url !== activeServer.url
        ? [[baseServer.url, activeServer.url]]
        : [],
    [baseServer, activeServer],
  );

  const active = snippets?.find((s) => s.id === language) ?? snippets?.[0];

  return (
    <div className="space-y-4">
      {/* Request line ---------------------------------------------------- */}
      <div className="overflow-hidden rounded-default border border-code-line bg-code-bg">
        <div className="flex items-center gap-2 border-b border-code-line px-3 py-2">
          <MethodChip method={method} size="md" />
          <span className="truncate font-mono text-[12px] text-code-text">
            {path}
          </span>
        </div>
        <div className="gt-scroll-dark overflow-x-auto px-3 py-2">
          <code className="whitespace-pre font-mono text-[11.5px] text-code-muted">
            {activeServer?.url}
            <span className="text-code-text">{path}</span>
          </code>
        </div>
      </div>

      {/* Environment ----------------------------------------------------- */}
      {service.servers.length > 1 ? (
        <div>
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-code-muted">
            Environment
          </p>
          <div
            role="radiogroup"
            aria-label="Environment"
            className="flex rounded-default border border-code-line bg-code-bg p-0.5"
          >
            {service.servers.map((server) => {
              const id = server.env ?? server.url;
              const selected = id === (activeServer?.env ?? activeServer?.url);
              return (
                <button
                  key={id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setEnv(id)}
                  title={server.description}
                  className={cn(
                    "flex-1 rounded-sm2 px-2 py-1 text-[11.5px] font-medium uppercase tracking-wide transition",
                    selected
                      ? "bg-accent text-accent-contrast"
                      : "text-code-muted hover:text-code-text",
                  )}
                >
                  {id}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* Snippet --------------------------------------------------------- */}
      {snippets?.length ? (
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-code-muted">
              Request
            </p>
          </div>

          <div className="gt-scroll-dark mb-2 flex gap-0.5 overflow-x-auto rounded-default border border-code-line bg-code-bg p-0.5">
            {snippets.map((snippet) => (
              <button
                key={snippet.id}
                type="button"
                onClick={() => setLanguage(snippet.id)}
                aria-current={snippet.id === active?.id}
                className={cn(
                  "shrink-0 rounded-sm2 px-2.5 py-1 text-[12px] font-medium transition",
                  snippet.id === active?.id
                    ? "bg-code-bg-3 text-code-text"
                    : "text-code-muted hover:text-code-text",
                )}
              >
                {snippet.label}
              </button>
            ))}
          </div>

          {active ? (
            <CodeBlock
              lines={active.lines}
              code={active.code}
              replace={replace}
              maxHeight="26rem"
            />
          ) : null}

          <p className="mt-1.5 flex items-start gap-1.5 text-[11.5px] leading-snug text-code-muted">
            <Terminal className="mt-0.5 size-3 shrink-0" aria-hidden />
            <span>
              Reads the token from <code>${TOKEN_ENV}</code>. Export it first so
              no credential is ever pasted into a snippet.
            </span>
          </p>
        </div>
      ) : null}

      {/* Responses ------------------------------------------------------- */}
      {responseExamples.length > 0 ? (
        <div>
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-code-muted">
            Response
          </p>
          <div className="space-y-2">
            {responseExamples.map((example) => (
              <CodeBlock
                key={example.status}
                lines={example.lines}
                code={example.lines
                  .map((line) => line.map((t) => t.c).join(""))
                  .join("\n")}
                filename={`${example.status} ${statusText(example.status)}`}
                maxHeight="20rem"
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

const STATUS_TEXT: Record<string, string> = {
  "200": "OK",
  "201": "Created",
  "202": "Accepted",
  "204": "No Content",
  "400": "Bad Request",
  "401": "Unauthorized",
  "403": "Forbidden",
  "404": "Not Found",
  "409": "Conflict",
  "429": "Too Many Requests",
  "500": "Internal Server Error",
};

const statusText = (status: string): string => STATUS_TEXT[status] ?? "";
